import { useEffect, useState } from "react";
import {
  Wallet,
  ChevronDown,
  Copy,
  LogOut,
  ExternalLink,
  Menu,
  KeyRound,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useLogin } from "@/hooks/useLogin";
import { useUserStore } from "@/store/useUserStore";
import { showInfo, showSuccess } from "@/lib/toast";
import { isDevelopment } from "@/config";
import InviteCodeDialog from "./InviteCodeDialog";
import logo from "@/assets/images/TradingSkill_Logo.png";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { SettingsPanel } from "@/components/SettingsPanel";
import PriceTicker from "./PriceTicker";

const C = {
  text: "#e0e6f0",
  muted: "#8892a8",
  dim: "#586580",
  primary: "#22c55e",
  green: "#22c55e",
  red: "#ef4444",
  bg: "#060810",
};

const EXPLORER_URL = isDevelopment
  ? "https://testnet.bscscan.com/address/"
  : "https://bscscan.com/address/";

function formatAddress(addr: string) {
  return `${addr.slice(0, 4)}...${addr.slice(-3)}`;
}

/**
 * 相对 forttrade 旧版 TopHeader 的变化（对齐 docx + forttrade-dapp）：
 * - 去掉 Header 中间 TSK 价格胶囊，改到下方 PriceTicker（BTC/ETH 实时 + TSK 模拟）
 * - 主行高度 h-[52px]，Logo h-7；连接按钮 12px + Wallet 14px；设置 36px + Menu 20px
 * - 底部 PriceTicker：marquee 80s（与 dapp 一致）
 */
export default function TopHeader() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { userInfo, email } = useUserStore();
  const {
    login,
    logout,
    isLoading,
    needInviteCode,
    setNeedInviteCode,
    isLoggedIn,
  } = useLogin();

  const displayAddress = userInfo?.address ?? "";
  const accountLabel = displayAddress ? formatAddress(displayAddress) : "";
  const isEmailAuth = userInfo?.authType === "email";

  useEffect(() => {
    setShowDropdown(false);
    setSettingsOpen(false);
  }, [location.pathname]);

  const handleWalletClick = () => {
    if (!isLoggedIn) {
      navigate("/login");
    }
  };

  const handleDisconnect = () => {
    setShowDropdown(false);
    logout();
    navigate("/login");
  };

  const handleCopy = async () => {
    if (!displayAddress) return;
    try {
      await navigator.clipboard.writeText(displayAddress);
      showSuccess(t("topHeader.addressCopied"));
    } catch {
      // ignore
    }
  };

  const handleExplorer = () => {
    if (!displayAddress) return;
    window.open(`${EXPLORER_URL}${displayAddress}`, "_blank");
  };

  const handleResetPassword = () => {
    setShowDropdown(false);
    navigate("/login", {
      state: {
        page: "forgot" as const,
        email: email ?? "",
      },
    });
  };

  const handleInviteCodeConfirm = async (inviteCode: string) => {
    await login(inviteCode);
    setNeedInviteCode(false);
  };

  const handleInviteCodeCancel = () => {
    setNeedInviteCode(false);
    logout({ silent: true });
    showInfo(t("topHeader.loginCancelled"));
  };

  const showAccountMenu = isLoggedIn;

  return (
    <div>
      <header
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          background: "rgba(6,8,16,0.92)",
          backdropFilter: "blur(24px) saturate(1.2)",
          borderBottom: "1px solid rgba(100,140,200,0.06)",
        }}
      >
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent 10%, rgba(34,197,94,0.07) 35%, rgba(33,150,246,0.04) 65%, transparent 90%)",
          }}
        />

        {/* 主栏：Logo 左 | 右：钱包 + 设置（无中间 TSK） */}
        <div className="px-3 sm:px-4 h-[52px] flex items-center justify-between gap-2">
          <div className="flex items-center shrink-0 min-w-0">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="rounded-lg -m-0.5 p-0.5 transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#22c55e]/50"
              aria-label={t("topHeader.backToHome")}
            >
              <img
                src={logo}
                alt="TradingSkill"
                className="h-7 w-auto object-contain max-w-[140px] sm:max-w-[168px]"
              />
            </button>
          </div>

          <div className="relative shrink-0 flex items-center gap-2">
            {!showAccountMenu ? (
              <button
                type="button"
                onClick={handleWalletClick}
                disabled={isLoading}
                className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-[12px] font-bold transition-all hover:opacity-90 active:scale-95 disabled:opacity-50"
                style={{
                  background: "linear-gradient(135deg, #22c55e, #00c8f0)",
                  color: C.bg,
                  boxShadow: "0 2px 10px rgba(34,197,94,0.18)",
                }}
              >
                <Wallet size={14} className="shrink-0" />
                <span className=" leading-none">{t("topHeader.login")}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg text-[12px] transition-all hover:opacity-90 active:scale-95 max-w-full"
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid rgba(34,197,94,0.18)",
                  color: C.primary,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                }}
              >
                <div
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{
                    background: C.primary,
                    boxShadow: `0 0 6px ${C.primary}`,
                  }}
                />
                <span
                  className="mono-num text-[11px] leading-none"
                  style={{ maxWidth: "78px", letterSpacing: "-0.02em" }}
                >
                  {accountLabel}
                </span>
                <ChevronDown
                  size={12}
                  className="shrink-0"
                  style={{ color: C.dim }}
                />
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                setShowDropdown(false);
                setSettingsOpen(true);
              }}
              className="flex items-center justify-center w-9 h-9 rounded-lg transition-all active:scale-90 shrink-0"
              style={{
                background: settingsOpen
                  ? "rgba(34,197,94,0.15)"
                  : "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
              aria-label={t("topHeader.settings")}
              aria-expanded={settingsOpen}
            >
              <Menu
                size={20}
                style={{
                  color: settingsOpen ? C.primary : C.text,
                }}
              />
            </button>

            {showDropdown && showAccountMenu && (
              <div
                className="absolute right-0 top-full mt-2 w-52 rounded-xl p-1.5 shadow-2xl"
                style={{
                  background: "rgba(12,16,32,0.96)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(100,140,200,0.10)",
                  boxShadow: "0 16px 48px rgba(0,0,0,0.65)",
                }}
              >
                <div className="px-3 py-2.5 mb-0.5">
                  <div
                    className="text-[10px] mb-1 uppercase tracking-wider leading-tight"
                    style={{ color: C.dim }}
                  >
                    {isDevelopment ? "BSC Testnet" : "BSC Mainnet"}
                  </div>
                  <div
                    className="mono-num text-[13px] font-semibold leading-tight"
                    style={{ color: C.primary }}
                  >
                    {accountLabel}
                  </div>
                </div>
                <div
                  className="h-px"
                  style={{ background: "rgba(100,140,200,0.06)" }}
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[12px] transition-colors hover:bg-white/4"
                  style={{ color: C.muted }}
                >
                  <Copy size={13} />
                  {t("topHeader.copyAddress")}
                </button>
                <button
                  type="button"
                  onClick={handleExplorer}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[12px] transition-colors hover:bg-white/4"
                  style={{ color: C.muted }}
                >
                  <ExternalLink size={13} />
                  {t("topHeader.blockExplorer")}
                </button>
                {isEmailAuth && (
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[12px] transition-colors hover:bg-white/4"
                    style={{ color: C.muted }}
                  >
                    <KeyRound size={13} />
                    {t("loginPage.resetPassword")}
                  </button>
                )}
                <div
                  className="h-px my-0.5"
                  style={{ background: "rgba(100,140,200,0.06)" }}
                />
                <button
                  type="button"
                  onClick={handleDisconnect}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[12px] transition-colors hover:bg-white/4"
                  style={{ color: C.red }}
                >
                  <LogOut size={13} />
                  {t("topHeader.disconnect")}
                </button>
              </div>
            )}
          </div>
        </div>

        <PriceTicker />
      </header>

      <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
        <SheetContent
          side="right"
          className="w-full max-w-md sm:max-w-md p-0 gap-0 z-100 border-l border-white/10 bg-[oklch(0.13_0.01_260)] flex flex-col overflow-hidden"
        >
          <SettingsPanel />
        </SheetContent>
      </Sheet>

      <InviteCodeDialog
        open={needInviteCode}
        onConfirm={handleInviteCodeConfirm}
        onCancel={handleInviteCodeCancel}
      />
    </div>
  );
}
